<template>
  <div id="film-posters">
    <!-- loading screen -->
    <transition name="fade" v-if="!notSupported.state">
      <div class="text-center">
        <v-dialog
          :value="loading"
          hide-overlay
          persistent
          width="300"
        >
          <v-card
            color="turquois"
            dark
            class="pt-2"
          >
            <v-card-text>
              <p class="mb-2 white--text text-center">Filmposter werden geladen ...</p>
              <v-progress-linear
                indeterminate
                color="white"
                class="mb-0"
              ></v-progress-linear>
            </v-card-text>
          </v-card>
        </v-dialog>
      </div>
    </transition>

    <!-- Browser Not Supported -->
    <transition name="fade">
      <modal-not-supported v-model="notSupported.showModal" />
    </transition>

    <!-- A-Frame scene -->
    <a-scene
      id="scene"
      ref="scene"
      v-if="arjsLoaded"
      embedded
      :arjs="`trackingMethod: best;
        debugUIEnabled: false;`"
      vr-mode-ui="enabled: false"
      cursor="fuse: false; rayOrigin: mouse"
      raycaster="objects: .clickable"
      renderer="logarithmicDepthBuffer: true;"
    >
      <!-- assets to be used -->
      <a-assets timeout="10000" @loaded="onAssetsLoaded" @timeout="onAssetsTimeout">
        <video
          :id="`v-${filmPoster.id}`"
          :src="filmPoster.video"
          webkit-playsinline
          playsinline
          crossorigin="anonymous"
          :muted="muted"
          v-for="filmPoster in filmPosters"
          :key="filmPoster.id"
        >
          Ihr Browser unterstützt das Videeo leider nicht. Versuchen Sie es mit einem anderen noch mal.
        </video>
      </a-assets>

      <!-- nft markers -->
      <a-nft
        :id="`m-${filmPoster.id}`"
        emitevents="true"
        type="nft"
        :url="filmPoster.markerSet"
        v-for="filmPoster in filmPosters"
        :key="filmPoster.id"
        smooth="true"
        smoothCount="10"
        smoothTolerance=".01"
        smoothThreshold="5"
      >
        <!-- video objects --> <!-- position="550 0 -90" height="360" width="640" -->
        <a-video
          :id="`a-${filmPoster.id}`"
          :class="filmPoster.data.active ? 'clickable' : ''"
          :src="'#v-' + filmPoster.id"
          position="550 0 -90"
          rotation="270 0 0"
          height="216"
          width="384"
          clickable
          :film-poster="`title: ${filmPoster.title}; fadeTime: ${fadeTime}; loadingOverlay: #l-${filmPoster.id}; muted: ${muted};`"
          material="opacity: 0"
          :animation__fade-in="`property: components.material.material.opacity;
            from: 0;
            to: 1;
            dur: ${fadeTime};
            startEvents: fade-in`"
          :animation__fade-out="`property: components.material.material.opacity;
            from: 1;
            to: 0;
            dur: ${fadeTime};
            startEvents: fade-out`"
        />

        <!-- loading overlay -->
        <a-plane
          :id="'l-' + filmPoster.id"
          src="/images/loading_overlay_smaller.png"
          position="550 20 -90"
          width="288"
          height="512"
          material="transparent: true"
          :animation__rotation="`property: rotation;
            from: 270 0 0;
            to: 270 0 360;
            dir: reverse;
            loop: true;
            dur: 700;
            easing: linear;
            autoplay: true`"
        />
        <!-- <a-entity
          gltf-model="https://arjs-cors-proxy.herokuapp.com/https://raw.githack.com/AR-js-org/AR.js/master/aframe/examples/image-tracking/nft/trex/scene.gltf"
          scale="5 5 5"
          position="500 0 0"
        >
        </a-entity> -->
      </a-nft>

      <!-- virtual camera -->
      <a-entity id="camera" camera />
    </a-scene>

    <!-- bottom button bar -->
    <div id="bottom-bar" v-if="cameraLoaded">
      <!-- console messages -->
      <!-- <div id="console">
        <div id="console-content" ref="console-content">
          <span v-for="(msg, key) in consoleMessages" :key="key">{{ msg }}<br></span>
        </div>
        <button @click="consoleMessages = []">Leeren</button>
      </div> -->

      <!-- play/pause button -->
      <v-btn
        @click="onPlayBtn"
        :disabled="!activeFilmPoster
          || activeFilmPoster.data.loading
          || activeFilmPoster.data.ended
          || activeFilmPoster.data.fading"
        id="play-button"
        class="bottom-bar-btn"
        fab
      >
        <!-- <img
          src="@/assets/images/film-posters/icons/pause.svg"
          alt="Pause"
          width="36"
          height="36"
          v-if="activeFilmPoster && activeFilmPoster.data.playing"
        > -->
        <v-icon v-if="activeFilmPoster && activeFilmPoster.data.playing">pause</v-icon>
        <!-- <img
          src="@/assets/images/film-posters/icons/play_arrow.svg"
          alt="Play"
          width="36"
          height="36"
          v-else
        > -->
        <v-icon v-else>play_arrow</v-icon>
      </v-btn>

      <!-- mute/unmute button -->
      <v-btn
        @click="onMuteBtn"
        id="mute-button"
        class="bottom-bar-btn"
        :class="{'muted': muted, 'pulse': !muteBtnPressed}"
        fab
      >
        <!-- <img
          src="@/assets/images/film-posters/icons/volume_off.svg"
          alt="Muted"
          width="20"
          height="20"
          v-if="muted"
        > -->
        <v-icon v-if="muted">volume_off</v-icon>
        <!-- <img
          src="@/assets/images/film-posters/icons/volume_up.svg"
          alt="Unmuted"
          width="20"
          height="20"
          v-else
        > -->
        <v-icon v-else>volume_up</v-icon>
      </v-btn>
    </div>
  </div>
</template>

<script>
import 'aframe'
import detectRTC from 'detectrtc'
import { lt } from 'semver'
import '@/aframe/components/common/clickable'
import '@/aframe/components/posters/filmposter'
import FilmPoster from '@/classes/filmposter'
import ModalNotSupported from '@/components/ModalNotSupported.vue'

export default {
  name: 'FilmPosters',
  components: {
    ModalNotSupported
  },
  data() {
    return {
      notSupported: {
        state: false,
        showModal: false,
        title: 'Upps, falscher Browser!',
        text: 'Es sieht so aus, als würdest du einen Browser benutzen, der unsere Inhalte nicht unterstützt.'
      },
      loading: true,
      arjsLoaded: false,
      cameraLoaded: false,
      muted: true,
      muteBtnPressed: false,
      fadeTime: 1500, // milliseconds
      consoleMessages: [],
      markersLoaded: 0,
      filmPosters: [
        new FilmPoster(
          '4e74c76dfeacea6e6fc8d812e28150a0',
          'Skatecop - Rache auf Rollen | Teaser Trailer',
          'https://arjs-cors-proxy.herokuapp.com/https://raw.githack.com/AR-js-org/AR.js/master/aframe/examples/image-tracking/nft/trex/trex-image/trex',
          'https://web.holonative.de/posters/skatecop/skatecop_teaser.mp4'
        )
      ]
    }
  },
  computed: {
    activeFilmPoster() {
      if (this.filmPosters.length === 0 || !this.filmPosters[0].data) return
      return this.filmPosters.find(filmPoster => {
        return filmPoster.data.active
      })
    },
    isIOSWebView() {
      // getUserMedia only works with Safari on iOS
      const ua = window.navigator.userAgent
      const iOS = !!ua.match(/iPad/i) || !!ua.match(/iPhone/i)
      if (!iOS) return false
      const isBrave = !!(navigator.brave) || false
      const isChrome = !!ua.match(/CriOS/i)
      const isFirefox = !!ua.match(/FxiOS/i)
      return !ua.match(/Safari/i) || isBrave || isChrome || isFirefox
    },
    isIOSWebAppWrongVersion() {
      // getUserMedia only works as iOS-PWA with iOS 13.4.1 and higher
      const iOS = !!window.navigator.userAgent.match(/iPad/i) ||
        !!window.navigator.userAgent.match(/iPhone/i)
      return iOS &&
        window.matchMedia('(display-mode: standalone)').matches &&
        lt(this.detectRTC.osVersion, '13.4.1')
    },
  },
  watch: {
    consoleMessages() {
      /* setTimeout(() => {
        this.$refs['console-content'].scrollTop = this.$refs['console-content'].scrollHeight
      }, 50) */
    }
  },
  methods: {
    onAssetsLoaded() {
      console.log('Assets loaded')
      this.consoleMessages.push('Assets loaded')

      // add additional properties and event listeners to film posters here
      for (let filmPoster of this.filmPosters) {
        // assign elements to film poster objects
        filmPoster.element = this.getElement('a', filmPoster.id)
        filmPoster.videoElement = this.getElement('v', filmPoster.id)
        filmPoster.markerElement = this.getElement('m', filmPoster.id)
        filmPoster.loadingElement = this.getElement('l', filmPoster.id)

        // sync vue object data with a-frame component data by setting reference
        filmPoster.element.addEventListener('loaded', e => {
          console.log('FP Component loaded')
          filmPoster.data = e.target.components['film-poster'].data
        })

        // listen for console logs
        filmPoster.element.addEventListener('consolemessage', msg => {
          console.log(msg.detail)
          this.consoleMessages.push(msg.detail)
        })
      }
    },
    onAssetsTimeout(err) {
      console.error('Zeitüberschreitung beim Laden der Assets', err.message)
      this.consoleMessages.push('Zeitüberschreitung beim Laden der Assets' + err.message)
    },
    // called on play button click
    onPlayBtn() {
      if (!this.activeFilmPoster) return
      this.activeFilmPoster.element.setAttribute('film-poster', 'userPaused', !this.activeFilmPoster.data.userPaused)
    },
    // called on mute button click
    onMuteBtn() {
      if (!this.muteBtnPressed) this.muteBtnPressed = true
      this.muted = !this.muted
    },
    getElement(prefix, id) {
      // prefixes: m = marker, v = video, a = film-poster, l = loading overlay
      return document.getElementById(`${prefix}-${id}`)
    }
  },
  created() {
    if (!detectRTC.isGetUserMediaSupported) {
      if (this.isIOSWebAppWrongVersion) {
        this.notSupported.title = "Upps, falsche iOS-Version!"
        this.notSupported.text = "Die AR-Filmposter werden von Ihrem Betriebssystem leider nicht unterstützt. Bitte aktualisieren Sie ihre iOS-Version."
      } else if (this.isIOSWebView) {
        this.notSupported.text = 'Die AR-Filmposter funktionieren auf Ihrem Gerät nur mit dem Safari-Browser. Bitte rufen Sie diese Seite damit erneut auf.'
      }
      this.notSupported.showModal = true
      this.notSupported.state = true
    }

  },
  async mounted() {
    if (this.notSupported.state) return
    setTimeout(() => {
      // attach arjs script to document
      let arjsScript = document.createElement('script')
      arjsScript.id = 'arjs-script'
      arjsScript.onload = () => {
        this.arjsLoaded = true
      }
      arjsScript.setAttribute('src', '/scripts/aframe-ar-nft.js') // regularly look for updates!
      document.head.appendChild(arjsScript)
    }, 1000)

    // hide loading screen when camera loaded
    window.addEventListener('arjs-video-loaded', () => {
      this.cameraLoaded = true
    })

    // hide loading element when nft markers loaded
    window.addEventListener('arjs-nft-loaded', () => {
      this.markersLoaded++
      if (this.markersLoaded === this.filmPosters.length) this.loading = false
    })
  }
}
</script>

<style>
body, #app {
  background: none !important;
  overflow: hidden !important;
  height: 100%;
}
#film-posters {
  height: 100%;
  position: relative
}

.fade-enter-active, .fade-leave-active {
  transition: opacity .3s ease;
}
.fade-enter, .fade-leave-to {
  opacity: 0;
}

#ar-wrapper {
  height: 100%;
  position: relative;
}
#film-posters .v-btn {
  background-color: var(--v-dark-base);
  color: #fff;
  display: flex;
  justify-content: center;
  align-items: center;
  border: 0;
}
#film-posters .v-btn:hover, .v-btn:focus, .v-btn.focus {
  box-shadow: none;
}
#film-posters .v-btn:active:not(.v-btn--disabled) {
  box-shadow: 0px 0px 11px 1px rgba(255, 255, 255, 0.5) !important;
}
#film-posters .v-btn--disabled {
  background-color: rgb(46, 49, 49, 0.5) !important;
}
#play-button {
  width: 60px;
  height: 60px;
}
#mute-button {
  width: 46px;
  height: 46px;
}
#mute-button * {
  pointer-events: none;
}
.muted {
  background-color: var(--v-pink-base) !important;
}
.pulse {
  animation: pulse 1s infinite;
}
@keyframes pulse {
  50% {
     transform:scale(1.3);
  }
}
#bottom-bar {
  position: fixed;
  bottom: 30px;
  left: 50%;
  transform: translateX(-50%);
  display:flex;
  align-items: center;
  justify-content: center;
}
.bottom-bar-btn {
  margin: 0 10px;
}
#console {
  width: 80vw;
  background-color: rgba(0, 48, 100, 0.7);
  padding: 8px;
  margin-bottom: 10px;
}
#console-content {
  color: #fff;
  height: 20vh;
  overflow-y: auto;
  border-bottom: 1px solid #ccc;
  margin-bottom: 8px;
  padding-bottom: 5px;
}
/* .video-thumbnail {
  position: relative;
  display: inline-block;
  cursor: pointer;
  margin: 30px;
}
.video-thumbnail:before {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  -webkit-transform: translate(-50%, -50%);
  content: "\f01d";
  font-size: 100px;
  color: #fff;
  opacity: 0.8;
  text-shadow: 0px 0px 30px rgba(0, 0, 0, 0.5);
}
.video-thumbnail:hover:before {
  color: #eee;
} */
#ar-viewer button:focus {
  box-shadow: none !important;
}
</style>
